import React, { useState } from 'react';

import { useCountdownTimer } from 'Util/Hooks';
import { useConfig } from 'Context/ConfigContext';

import AssessmentContext from './context';
import { SESSION_STATUS } from './constant';

/**
 * This is the engine for an assessment session such as a quiz and exam.
 * @param {Object} metaData - contains data of the assessment and session such as sessionId, totalQuestions, assessmentName, limitiedTime, etc.
 * @param {string} metaData.id - id of the session depends on platform (e.g. examSessionId for Anywhere)
 * @param {number} metaData.totalQuestions - total question of the whole session
 * @param {Object[]} questions - contain question objects
 * */

/**
 * Status and function normal flow:
 * NOT_STARTED => startSession() => IN_PROGRESS => "questions are out of range" => CONFIRMING_ANSWER => endSession() => READY_TO_GRADE => gradePaper() => GRADED
 */

const AssessmentProvider = ({
  onStartSession,
  onEndSession,
  onGradePaper,
  onSubmitAnswer,
  timeLimit,
  children,
}) => {
  const [metaData, setMetaData] = useState(null);
  const [questions, setQuestions] = useState(null);
  const [status, setStatus] = useState(SESSION_STATUS.NOT_STARTED);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(null);
  const [hasRequestSubmitGradedExam, setRequestSubmitGradedExam] = useState(false);
  const { classroom } = useConfig();
  const enableSubmitAnswerOnEachQuestion =
    classroom?.quiz?.enableSubmitAnswerOnEachQuestion ?? true;

  const startSession = (metaData, questions) => {
    setMetaData(metaData);
    setQuestions(questions);
    setCurrentQuestionIndex(0);
    setStatus(SESSION_STATUS.IN_PROGRESS);
    onStartSession();
  };

  const endSession = () => {
    setStatus(SESSION_STATUS.GRADE);
    onEndSession();
  };

  const gradePaper = () => {
    setStatus(SESSION_STATUS.GRADED);
    onGradePaper();
  };

  const clear = () => {
    setMetaData(null);
    setQuestions(null);
    setCurrentQuestionIndex(null);
  };

  const submitAnswer = async (question, answer) => {
    const currentQuestion = getCurrentQuestion();
    currentQuestion.selectedAnswer = answer;

    const newQuestions = [...questions];
    newQuestions[currentQuestionIndex] = currentQuestion;

    setQuestions(newQuestions);

    // Enable call GraphQL when submit answer on each question
    if (enableSubmitAnswerOnEachQuestion) {
      await onSubmitAnswer(question, answer);
    }
  };

  const goToNextQuestion = () => {
    if (questions && questions.length > currentQuestionIndex + 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const getCurrentQuestion = () => {
    if (questions && questions.length >= currentQuestionIndex + 1) {
      const currentQuestion = { ...questions[currentQuestionIndex] };
      return currentQuestion;
    }
  };

  //Assume timeLimit = 0  which mean unlimit
  const countdownTime = timeLimit === 0 ? undefined : timeLimit;
  const [timeRemaining] = useCountdownTimer(countdownTime, false, {
    onTimeout: () => {
      setStatus(SESSION_STATUS.TIMEOUT);
    },
  });

  return (
    <AssessmentContext.Provider
      value={{
        status,
        setStatus,
        currentQuestionIndex,
        setCurrentQuestionIndex,
        metaData,
        setMetaData,
        questions,
        setQuestions,
        startSession,
        endSession,
        gradePaper,
        clear,
        goToNextQuestion,
        goToPreviousQuestion,
        currentQuestion: getCurrentQuestion(),
        getCurrentQuestion,
        submitAnswer,
        timeLimit,
        timeRemaining: timeLimit ? timeRemaining : undefined,
        hasRequestSubmitGradedExam,
        setRequestSubmitGradedExam,
      }}
    >
      {children}
    </AssessmentContext.Provider>
  );
};

AssessmentProvider.defaultProps = {
  onStartSession: () => {},
  onEndSession: () => {},
  onGradePaper: () => {},
  onSubmitAnswer: () => {},
};

export default AssessmentProvider;
